// ActionDock — «Док действий», выезжающий снизу при drag карточки «Картотека».
//
// Виден ТОЛЬКО при dragSession.isDragging (translate-y-snug 200–300ms, уважает
// reduced-motion). В покое уведён за край и `pointer-events-none` — не перехватывает
// клики. Реестр действий `DockAction[]` (сейчас одно — `add-to-collection`),
// архитектура расширяемая: новое действие = ещё одна секция дока.
//
// Геометрия (rules/frontend-ui §25.1): fixed, вписан между sidebar и правой
// side-panel, над BottomNav + AudioMiniPlayer + safe-area. z выше обоих
// (AudioMiniPlayer z-40, BottomNav z-30) → z-(--sh-z-overlay).

import { IconFolderPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { useDragSessionStore } from "@/stores/drag-session";
import { CollectionChips } from "./collection-chips";

export function ActionDock(): React.ReactElement {
	const { t } = useTranslation("library");
	const isDragging = useDragSessionStore((s) => s.isDragging);

	return (
		<div
			data-action-dock=""
			aria-hidden={!isDragging}
			// Док вне перетаскивания скрыт прозрачностью, а не размонтирован, —
			// его кнопки-цели остаются фокусируемыми. Без парного `inert` браузер
			// такой `aria-hidden` не применяет, и с клавиатуры можно уйти в
			// невидимый док.
			inert={!isDragging ? true : undefined}
			className={cn(
				"fixed z-(--sh-z-overlay)",
				// Вписан в рабочую область: левее sidebar, правее side-panel, над
				// нижними оверлеями (плеер + safe-area). На mobile нижнее меню на
				// время drag скрыто (MainLayout) → --bottom-nav-height=0, Док чисто
				// занимает низ, перекрытия меню нет.
				"left-[var(--sidebar-width,0px)] right-[var(--side-panel-width,0px)]",
				"bottom-[calc(var(--bottom-nav-height,0px)+var(--audio-player-height,0px)+env(safe-area-inset-bottom,0px))]",
				"px-3 pb-3",
				// Показ/скрытие — мгновенно через opacity, БЕЗ transform и БЕЗ transition.
				// КРИТИЧНО: transform на этом блоке (даже статичный translate-y-0) создаёт
				// backdrop-root → backdrop-blur вложенной карточки перестаёт видеть фон
				// страницы и не рендерится («блюра не ощущалось»). transition opacity тоже
				// нельзя — во время фейда (opacity<1) backdrop-root создаётся, блюр «догоняет».
				// Поэтому жёсткий jump 0↔1 (мы уже внутри жеста drag) — блюр виден сразу.
				isDragging ? "opacity-100" : "pointer-events-none opacity-0",
			)}
		>
			{/* Поверхность дока намеренно НЕ как карточка: крупный радиус, градиентная
			    заливка primary→accent, цветное кольцо + glow — явно отдельный слой над
			    страницей (фидбек: «не сливается с карточками, выше, заметнее»). */}
			<div className="mx-auto max-w-5xl rounded-3xl border border-primary/25 bg-linear-to-t from-primary/12 via-card/80 to-accent/10 p-4 shadow-e3 ring-1 ring-primary/15 backdrop-blur-xl backdrop-saturate-150">
				<div className="flex flex-col gap-3">
					<div className="flex items-center gap-2 text-sm font-semibold text-foreground">
						<span className="flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-primary/15 text-primary">
							<IconFolderPlus size={16} stroke={1.75} />
						</span>
						<span>{t("dock.dropTitle")}</span>
					</div>
					<CollectionChips />
				</div>
			</div>
		</div>
	);
}
